/* eslint-disable @typescript-eslint/no-explicit-any */
/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
import { NOT_APPLICABLE } from '@config/constants/common';
import { TApiParams, TParams } from '@global-types/index';
import { apiClient } from '@lib/api-client';
import useStore from '@store/index';
import { useQuery } from '@tanstack/react-query';
import { getCountryName } from '@utils/countries';
import { isNumber } from '@utils/functions';
import { getTechnologyProject } from '@utils/project-technologies';
import { TApiResponse } from '../types';

/* -------------------------------------------------------------------------- */
/*                                     Type                                   */
/* -------------------------------------------------------------------------- */

export type TParsedAdditionalityProject = {
	supportedBy?: string;
	entity: string;
	name: string;
	unit: string;
	technology: string;
	__t: string;
	sellPrice_europertco2eq: number;
	costPrice_europertco2eq: number;
	quantity: {
		total_aco2: number;
		held_aco2: number;
		burnt_aco2: number;
		remaining_aco2: number;
		unprocessed_gco2eq: number;
	};
	organization: string;
	location: {
		countryCode: string;
		place: string;
	};
	country?: {
		name: string;
		code: string;
	};
	region?: string;
	type?: string;
	createdAt: string;
	updatedAt: string;
	id: string;
	tokenAvailable?: string;
	quantitytco2eq?: number;
	cover?: string;
	priceTco2?: string;
	image?: string;
	isDisabled: boolean;
};

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */

export const getPublicAdditionalityProjects = (
	params: TApiParams
): Promise<TApiResponse> => {
	const { queryParams, page, pageSize } = params;
	const { name, ...restQueryParams } = queryParams;
	return apiClient.get('/public/additionalities', {
		params: {
			page: page,
			pageSize: pageSize,
			query: restQueryParams,
			sort: '-supportedBy -active -financing.unlimited -financing.remaining_euro name',
			active: true,
			...(name && {
				search: name,
				searchFields: ['name', 'location.place'],
			}),
			populate:
				'cover, logo, publicSpecs, supportedBy.logo, organization',
		},
	});
};

/* -------------------------------------------------------------------------- */
/*                                    Hooks                                   */
/* -------------------------------------------------------------------------- */
export const useAdditionalityProjects = (
	page: number,
	size: number,
	queryParams: TParams
) => {
	const { getUser } = useStore();
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const user: any = getUser();

	const { data, isFetching, isFetched, isPending } =
		useQuery({
			queryKey: [
				'additionality-projects',
				{
					page: page,
					size: size,
					queryParams: queryParams,
				},
			],
			queryFn: () => {
				return getPublicAdditionalityProjects({
					page: page,
					pageSize: size,
					queryParams: queryParams,
				});
			},
			staleTime: 0,
		});

	const updateList = (projects: any) =>
		projects.reduce((acc: any, currentItem: any) => {
			const technolgyProject = getTechnologyProject(
				currentItem.technology
			);
			const countryName = getCountryName(
				currentItem.location.countryCode
			);

			const unlimited =
				currentItem.financing &&
				currentItem.financing.unlimited
					? currentItem.financing.unlimited
					: 0;
			const financialNeeds = unlimited
				? 'Unlimited'
				: currentItem.financing &&
					  isNumber(currentItem.financing.target_euro)
					? currentItem.financing &&
						isNumber(currentItem.financing.target_euro)
						? currentItem.financing.target_euro
						: NOT_APPLICABLE
					: 0;
			const coverage =
				currentItem.financing &&
				isNumber(currentItem.financing.pledged_euro)
					? currentItem.financing.pledged_euro.toString()
					: 0;

			const percent =
				currentItem.financing &&
				isNumber(currentItem.financing.target_euro) &&
				isNumber(currentItem.financing.remaining_euro)
					? ((currentItem.financing.target_euro -
							currentItem.financing.remaining_euro) /
							currentItem.financing.target_euro) *
						100
					: 0;

			const progress = unlimited
				? 40
				: Math.round(percent);

			const cover =
				currentItem && currentItem.cover
					? currentItem.cover
					: technolgyProject
						? technolgyProject.image
						: '';

			const modifiedItem = {
				...currentItem,
				isPool: currentItem?.isPool,
				id: currentItem.id,
				name: currentItem.name,
				financing: currentItem.financing,
				country: {
					name: countryName,
					code: currentItem.location.countryCode,
				},
				region: currentItem.location.place,
				type: technolgyProject
					? technolgyProject.label
					: '',
				image: cover,
				cover,
				price: 0,
				financialNeeds: financialNeeds,
				coverage: coverage,
				remaining_euro: currentItem?.financing
					?.remaining_euro
					? currentItem?.financing.remaining_euro
					: 0,
				progress: progress,
				supportedBy: currentItem?.supportedBy?.logo,
				isDisabled:
					Number(financialNeeds) - Number(coverage) == 0,
				active: currentItem?.active,
				organization:
					currentItem?.organization?.name ||
					NOT_APPLICABLE,
			};
			acc.push(modifiedItem);

			return acc;
		}, []);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		isFetching,
		isPending,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
